import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import AdminToolbar from '../admin/admin_toolbar';
import AssistantHeader from './header';
import AssistantLauncher from './launcher';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Conversation } from '../conversation';
import { ConversationProvider, useConversationContext } from '../conversation_context';
import { getPageContext } from '../hooks/use_assistant';
import { DropOverlay } from '../drop_overlay';
import { OPEN_ASSISTANT_EVENT, type OpenAssistantOptions } from '../events';
import { NextActionsProvider, useNextActionsContext, withNextActions } from '../next_actions_context';
import { PinnedRecommendations } from '../tools/inline/recommendation_cards';
import { StandaloneTools } from '../tools/standalone';
import { ToolsProvider } from '../tools/context';

import type { Agent, Paths } from '../types';

interface Props {
  agents: Agent[];
  currentUser: {
    admin: boolean;
    firstName?: string | null;
  };
  paths: Paths;
}

// Anchor positioning keeps the panel clear of the main nav where it's supported; elsewhere it just
// sits at the top of the viewport.
const PANEL_TOP = 'calc(max(0px, anchor(--main-nav bottom)) + 0.75rem)';
const panelTop = typeof CSS !== 'undefined' && CSS.supports('top', PANEL_TOP) ? PANEL_TOP : '0.75rem';

// Session-scoped like the chat itself: an open panel survives navigations in this tab, while a new
// tab still starts closed.
const OPEN_KEY = 'aiAssistantOpen';

function readOpenState(): boolean {
  try {
    return window.sessionStorage.getItem(OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeOpenState(open: boolean) {
  try {
    if (open) {
      window.sessionStorage.setItem(OPEN_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(OPEN_KEY);
    }
  } catch {
    // The panel just starts closed on the next page.
  }
}

export default function AiAssistant({ agents, currentUser, paths }: Props) {
  const [open, setOpen] = useState(readOpenState);

  function changeOpen(next: boolean) {
    setOpen(next);
    writeOpenState(next);
  }

  return (
    <ToolsProvider>
      <NextActionsProvider>
        <AssistantConversation
          agents={agents}
          currentUser={currentUser}
          open={open}
          paths={paths}
          onChangeOpen={changeOpen}
        />
      </NextActionsProvider>

      <StandaloneTools/>
    </ToolsProvider>
  );
}

function AssistantConversation({ agents, currentUser, open, paths, onChangeOpen }: Props & {
  open: boolean;
  onChangeOpen: (open: boolean) => void;
}) {
  const nextActions = useNextActionsContext();

  return (
    <ConversationProvider
      agents={agents}
      firstName={currentUser.firstName}
      isAdmin={currentUser.admin}
      pageContext={withNextActions(nextActions, getPageContext)}
      paths={paths}
      visible={open}
    >
      {!open && <AssistantLauncher onClick={() => onChangeOpen(true)} />}

      <SidebarPanel
        collapsed={!open}
        onCollapse={() => onChangeOpen(false)}
        onExpand={() => onChangeOpen(true)}
      />
    </ConversationProvider>
  );
}

interface PanelProps {
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
}

function SidebarPanel({ collapsed, onCollapse, onExpand }: PanelProps) {
  const { getRootProps, isDragActive, send } = useConversationContext();

  const onExpandRef = useRef(onExpand);
  onExpandRef.current = onExpand;

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OpenAssistantOptions>).detail;

      onExpandRef.current();
      if (detail?.prompt) {
        send(detail.prompt);
      }
    };

    window.addEventListener(OPEN_ASSISTANT_EVENT, handler);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, handler);
  }, [send]);

  return (
    // Kept mounted while collapsed so the transcript and any turn still streaming survive a close
    <div
      className={classNames('position-fixed', { 'd-none': collapsed })}
      style={{ bottom: '0.75rem', right: '0.75rem', top: panelTop, width: '360px', zIndex: 1045 }}
    >
      <Card className="ai-assistant d-flex flex-column h-100 overflow-hidden" {...getRootProps()}>
        {isDragActive && <DropOverlay/>}

        <AssistantHeader onClose={onCollapse} />
        <AdminToolbar />
        <Conversation pinned={<PinnedRecommendations />} />
      </Card>
    </div>
  );
}
